import * as Sentry from "@sentry/react";
import { WhiteboardPermission } from "@src/gql/graphql";
import {
	showErrorNotification,
	showSuccessNotification,
} from "@src/utils/notifications";
import type { z } from "zod";
import type { Role, userSelected } from "./whiteboard-invite-schema";

export type GrantEntityPermission = (params: {
	entityId: string;
	userEmails: string[];
	permission: WhiteboardPermission;
}) => Promise<unknown>;

export type RevokeEntityPermission = (params: {
	entityId: string;
	userEmail: string;
}) => Promise<unknown>;

export type TransferEntityOwnership = (params: {
	entityId: string;
	newOwnerEmail: string;
}) => Promise<unknown>;

/**
 * Detect users whose permissions have changed or who have been removed
 */
export function detectUserChanges(
	previousUsers: z.infer<typeof userSelected>[],
	currentUsers: z.infer<typeof userSelected>[],
): {
	changedUsers: {
		user: z.infer<typeof userSelected>;
		oldPermission: Role;
		newPermission: Role;
	}[];
	removedUsers: z.infer<typeof userSelected>[];
} {
	const changedUsers = [];
	const removedUsers = [];

	for (const existingUser of previousUsers) {
		const matchingUser = currentUsers.find(
			(user) => user.email === existingUser.email,
		);

		if (!matchingUser) {
			removedUsers.push(existingUser);
		} else if (matchingUser.permission !== existingUser.permission) {
			changedUsers.push({
				user: existingUser,
				oldPermission: existingUser.permission,
				newPermission: matchingUser.permission,
			});
		}
	}

	return { changedUsers, removedUsers };
}

/**
 * Generate API calls for permission changes and removals
 */
export function generatePermissionUpdates(
	changedUsers: {
		user: z.infer<typeof userSelected>;
		oldPermission: Role;
		newPermission: Role;
	}[],
	removedUsers: z.infer<typeof userSelected>[],
	entityId: string,
	grantPermission: GrantEntityPermission,
	revokePermission: RevokeEntityPermission,
	transferOwnership: TransferEntityOwnership,
): Promise<unknown>[] {
	const promises = [];

	if (removedUsers.length > 0) {
		for (const user of removedUsers) {
			promises.push(
				revokePermission({
					entityId,
					userEmail: user.email,
				}),
			);
		}
	}

	if (changedUsers.length > 0) {
		for (const user of changedUsers) {
			switch (user.newPermission) {
				case "Viewer":
					promises.push(
						grantPermission({
							entityId,
							userEmails: [user.user.email],
							permission: WhiteboardPermission.Read,
						}),
					);
					break;
				case "Editor":
					promises.push(
						grantPermission({
							entityId,
							userEmails: [user.user.email],
							permission: WhiteboardPermission.Write,
						}),
					);
					break;
				case "Remove":
					promises.push(
						revokePermission({
							entityId,
							userEmail: user.user.email,
						}),
					);
					break;
				case "Owner":
					promises.push(
						transferOwnership({
							entityId,
							newOwnerEmail: user.user.email,
						}),
					);
					break;
			}
		}
	}

	return promises;
}

/**
 * Execute permission update promises and handle notifications
 */
export async function executePermissionUpdates(
	promises: Promise<unknown>[],
): Promise<boolean> {
	if (promises.length === 0) {
		return true;
	}

	try {
		await Promise.all(promises);
		showSuccessNotification("Success", "User permissions updated");
		return true;
	} catch (error) {
		Sentry.captureException(error);
		showErrorNotification("Error", "Failed to update user permissions");
		return false;
	}
}
